import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Styles
import './groups.scss';

// Components
import GroupSelection from './group-selection';
import Button, { BUTTON_STYLES } from '../button/button';
import Tooltip from '../tooltip/tooltip';

// Actions
import consoleActions from '../../containers/console/console-actions';
import courseActions from '../../containers/console/actions/courses-actions';
import groupActions from '../../containers/console/actions/group-actions';

// Context
import CourseInformationContext from './context/course-information-context';

// Hooks
import useOutsideClick from '../../hooks/use-outside-click';
import { DEFAULT_UPF_COURSE_ID } from '../../helpers/enums';

function Groups(props) {
    const { i18n } = useLocalisation();

    // Props
    const {
        parentClass,
        classGroups,
        currentClassId,
        productId,
        isFetchingEditInformation,
        hasContentLoaded,
        // Function Handlers
        updateCurrentClassId,
        addNewGroup,
        editGroupName,
        deleteExistingGroup,
        editGroupCourse,
        getDefaultCourseInformation,
        getEditGroupCourseInformation,
        getDefaultCourse,
        getCourseCountries,
        getCourseYears,
        getCourses,
        updateGroupCheckbox,
        resetYearOptions,
        resetEditInformationHandler,
        // Course Specific options
        // - Country
        setCountryHandler,
        countrySelectValue,
        countrySelectOptions,
        // - Year
        setYearHandler,
        yearSelectValue,
        yearSelectOptions,
        // - Course
        setCourseHandler,
        courseSelectValue,
        courseSelectOptions,
        // - UPF Course
        setUPFCourseHandler,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        // - New Courses
        setNewCourseHandler,
        newCourseSelectValue,
        newCourseSelectOptions,
        showNewCourses,
        newCoursesAvailabilityCriteria,
        setShownPassword,
        // - Groups
        createMultiYearGroups,
        resetMultiYearGroupFetch
    } = props;

    const groupRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const classroomId = currentClassId;

    const completeCallback = useCallback(
        async () => {
            const updateResponse = await updateCurrentClassId({
                productId,
                classId: classroomId
            });

            // eslint-disable-next-line no-underscore-dangle
            if (window.__SKIP_GROUP_COLLAPSE__) {
                // eslint-disable-next-line no-underscore-dangle
                window.__SKIP_GROUP_COLLAPSE__ = false;
                return;
            }

            // If the button was pressed, reset it
            if (isExpanded === true && updateResponse === true) {
                setIsExpanded(false);
            }
        },
        [classroomId, isExpanded]
    );
    // Handlers (local specifically)
    const expandButtonHandler = () => setIsExpanded(prevState => !prevState);

    // To retrieve data for existing group when EDIT group is cliked
    const localGetGroupInformation = ({ groupId }) => getEditGroupCourseInformation({
        classId: classroomId,
        productId,
        groupId
    });
    // to get default course data when NEW GROUP button is clicked
    const localDefaultCourseInformation = ({ defaultData }) => getDefaultCourseInformation({
        defaultData
    });

    // Responsible for handling the new group button
    const newGroupHandler = async ({ name }) => {
        let callback;
        // Create a new group first
        let value = await addNewGroup({
            classroomId,
            groupName: name,
            courseId: parseInt(courseSelectValue, 10),
            upfCourseId: upfCourseSelectOptions.length > 1 ? parseInt(upfCourseSelectValue, 10) : DEFAULT_UPF_COURSE_ID,
            newCourseId: newCourseSelectValue
        });

        const isNewGroupValid = value && value.status >= 200 && value.status < 300;
        // Update the course afterwards a valid response
        if (isNewGroupValid) {
            value = true;
            callback = () => completeCallback();
        }

        return {
            value,
            callback
        };
    };
    // Responsible for handling the edit group button
    const editGroupHandler = async ({ name, groupId }) => {
        let value;
        let callback = null;

        // Validate that the group name and new group name are the same, if they are, don't change the name
        let isExistingGroupValid = true;
        if (name) {
            value = await editGroupName({
                classroomId,
                groupId,
                groupName: name
            });
            // Afterwards update the course of the group
            isExistingGroupValid = value && value.status >= 200 && value.status < 300;
        }

        // Update the course afterwards a valid response
        if (isExistingGroupValid) {
            const courseId = parseInt(courseSelectValue, 10);
            const upfCourseId = upfCourseSelectOptions.length > 1 ?
                parseInt(upfCourseSelectValue, 10) : DEFAULT_UPF_COURSE_ID;
            const newCourseId = newCourseSelectValue;

            value = await editGroupCourse({
                classroomId,
                groupId,
                courseId,
                upfCourseId,
                newCourseId
            });

            // If value is valid, retrieve the list of students/groups again within the current classId
            if (value === true) {
                callback = () => completeCallback();
            }
        }

        return { value, callback };
    };
    const deleteGroupHandler = async ({ groupId }) => {
        const response = await deleteExistingGroup({
            classroomId,
            groupId
        });

        if (response === true) {
            return () => completeCallback();
        }

        return response;
    };

    const groupContainerRef = useRef(null);
    useOutsideClick(
        [groupContainerRef],
        () => setIsExpanded(false)
    );

    const courseInformationValues = {
        // Select Options
        // - Country
        setCountryHandler,
        countrySelectValue,
        countrySelectOptions,
        // - Year
        setYearHandler,
        yearSelectValue,
        yearSelectOptions,
        // - Course
        setCourseHandler,
        courseSelectValue,
        courseSelectOptions,
        // - UPF Course
        setUPFCourseHandler,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        // - New Courses
        setNewCourseHandler,
        newCourseSelectValue,
        newCourseSelectOptions
    };

    return (
        <React.Fragment>
            <div ref={groupContainerRef} className={`${parentClass} groups`}>
                <h2 className={`console__title ${isExpanded ? 'console__title--expanded' : ''}`}>
                    <Trans id={'Class Group Selection - header'} />
                    <Tooltip visibleTexts={[i18n._(t`Tooltips - groups`)]} />
                </h2>
                <Button
                    importedClasses={`groups__button ${isExpanded ? 'groups__button--expanded' : ''}`}
                    buttonStyle={BUTTON_STYLES.iconSVG}
                    iconName={isExpanded ? 'groupminimise' : 'groupmaximise'}
                    ariaProps={
                        {
                            'aria-hidden': true,
                            'aria-label': i18n._(t`Groups - expand`),
                            tabIndex: -1
                        }
                    }
                    callback={expandButtonHandler}
                />
                <CourseInformationContext.Provider value={courseInformationValues}>
                    <GroupSelection
                        ref={groupRef}
                        productId={productId}
                        currentClassId={currentClassId}
                        importedClasses={isExpanded ? 'groups__filters--expanded' : ''}
                        classGroups={classGroups}
                        isFetchingEditInformation={isFetchingEditInformation}
                        resetEditInformationHandler={resetEditInformationHandler}
                        showNewCourses={showNewCourses}
                        newCoursesAvailabilityCriteria={newCoursesAvailabilityCriteria}
                        // Function Handlers
                        newGroupHandler={newGroupHandler}
                        editGroupHandler={editGroupHandler}
                        deleteGroupHandler={deleteGroupHandler}
                        getCourseYears={getCourseYears}
                        getDefaultCourse={getDefaultCourse}
                        getCourseCountries={getCourseCountries}
                        getCourses={getCourses}
                        getDefaultCourseInformation={localDefaultCourseInformation}
                        getEditGroupCourseInformation={localGetGroupInformation}
                        resetYearOptions={resetYearOptions}
                        updateGroupCheckbox={updateGroupCheckbox}
                        setShownPassword={setShownPassword}
                        createMultiYearGroups={createMultiYearGroups}
                        updateCurrentClassId={updateCurrentClassId}
                        hasContentLoaded={hasContentLoaded}
                        resetMultiYearGroupFetch={resetMultiYearGroupFetch}
                    />
                </CourseInformationContext.Provider>
            </div>
        </React.Fragment>
    );
}

Groups.propTypes = {
    parentClass: PropTypes.string,
    classGroups: PropTypes.array,
    productId: PropTypes.number,
    currentClassId: PropTypes.number,
    isFetchingEditInformation: PropTypes.bool,
    hasContentLoaded: PropTypes.bool,
    // Handlers
    updateGroupCheckbox: PropTypes.func,
    updateCurrentClassId: PropTypes.func,
    getDefaultCourseInformation: PropTypes.func,
    getEditGroupCourseInformation: PropTypes.func,
    getDefaultCourse: PropTypes.func,
    getCourseCountries: PropTypes.func,
    getCourseYears: PropTypes.func,
    getCourses: PropTypes.func,
    addNewGroup: PropTypes.func,
    editGroupName: PropTypes.func,
    deleteExistingGroup: PropTypes.func,
    editGroupCourse: PropTypes.func,
    resetYearOptions: PropTypes.func,
    resetEditInformationHandler: PropTypes.func,
    // Course Specific options
    // - Country
    setCountryHandler: PropTypes.func,
    countrySelectValue: PropTypes.string,
    countrySelectOptions: PropTypes.array,
    // - Year
    setYearHandler: PropTypes.func,
    yearSelectValue: PropTypes.string,
    yearSelectOptions: PropTypes.array,
    // - Course
    setCourseHandler: PropTypes.func,
    courseSelectValue: PropTypes.string,
    courseSelectOptions: PropTypes.array,
    // - UPF Course
    setUPFCourseHandler: PropTypes.func,
    upfCourseSelectValue: PropTypes.string,
    upfCourseSelectOptions: PropTypes.array,
    // - New Courses
    setNewCourseHandler: PropTypes.func,
    newCourseSelectValue: PropTypes.string,
    newCourseSelectOptions: PropTypes.array,
    showNewCourses: PropTypes.bool,
    newCoursesAvailabilityCriteria: PropTypes.object,
    setShownPassword: PropTypes.func,
    // -Groups
    createMultiYearGroups: PropTypes.func,
    resetMultiYearGroupFetch: PropTypes.func
};

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from MessageModalCombinedReducers.
 */
function mapStateToProps(state) {
    const {
        countrySelectValue,
        countrySelectOptions,
        yearSelectValue,
        yearSelectOptions,
        courseSelectValue,
        courseSelectOptions,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        newCourseSelectValue,
        newCourseSelectOptions,
        currentClassId,
        productId,
        isFetchingEditInformation,
        hasContentLoaded
    } = state.consoleReducer;

    return {
        productId,
        currentClassId,
        countrySelectValue,
        countrySelectOptions,
        yearSelectValue,
        yearSelectOptions,
        courseSelectValue,
        courseSelectOptions,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        newCourseSelectValue,
        newCourseSelectOptions,
        isFetchingEditInformation,
        hasContentLoaded
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        updateCurrentClassId: consoleActions.updateCurrentClassId,
        setCountryHandler: courseActions.setCountryHandler,
        setYearHandler: courseActions.setYearHandler,
        setCourseHandler: courseActions.setCourseHandler,
        setUPFCourseHandler: courseActions.setUPFCourseHandler,
        setNewCourseHandler: courseActions.setNewCourseHandler,
        getDefaultCourse: courseActions.getDefaultCourse,
        getCourseCountries: courseActions.getCourseCountries,
        getCourseYears: courseActions.getCourseYears,
        resetYearOptions: courseActions.resetYearOptions,
        getCourses: courseActions.getCourses,
        addNewGroup: groupActions.addNewGroup,
        editGroupName: groupActions.editGroupName,
        deleteExistingGroup: groupActions.deleteExistingGroup,
        editGroupCourse: groupActions.editGroupCourse,
        updateGroupCheckbox: groupActions.updateGroupCheckbox,
        resetEditInformationHandler: groupActions.resetEditInformationHandler,
        getEditGroupCourseInformation: groupActions.getEditGroupCourseInformation,
        getDefaultCourseInformation: groupActions.getDefaultCourseInformation,
        createMultiYearGroups: groupActions.createMultiYearGroups,
        resetMultiYearGroupFetch: groupActions.resetMultiYearGroupFetch
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Groups);



// WEBPACK FOOTER //
// ./components/groups/groups.js